import { isDefined } from '@sgme/fp';
import Toast from 'react-bootstrap/Toast';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { useCurrentUserQuery } from '@/api/user/index.ts';
import { GrowlError } from '@/App/Growl/shared/GrowlError.tsx';
import { GrowlIntegrationDelay } from '@/App/Growl/shared/GrowlIntegrationDelay.tsx';
import { GrowlItem } from '@/App/Growl/shared/GrowlItem.tsx';
import { getExecutionColorAndIcon } from '@/App/Growl/utilities.ts';
import { FormattedFXDate } from '@/components/ui/FormattedFXDate.tsx';
import { Panel } from '@/components/ui/Panel/index.tsx';
import { blotterSlice } from '@/store/blotter/slice.ts';
import type { SwapDealExecution } from '@/store/growl/state/swap.ts';
import { useAppSelector } from '@/store/hooks.ts';

import { GrowlLegs } from './GrowlLegs.tsx';

export type GrowlSwapProps = {
  growlItem: SwapDealExecution;
};

export const GrowlSwap = (props: GrowlSwapProps) => {
  const { growlItem } = props;

  const { formatNumber } = useIntl();

  const { data: currentUser } = useCurrentUserQuery();

  const accountName = isDefined(currentUser)
    ? currentUser.clients.find((client) => client.companyId === growlItem.data.accountId)?.companyName
    : undefined;

  const isExternalUser = isDefined(currentUser) && currentUser.userType === 'Trader';

  const [color, icon] = getExecutionColorAndIcon(growlItem.status);

  const effectiveWay =
    growlItem.data.way === 'Bid'
      ? growlItem.data.nearLeg.currencyIndex === 1
        ? 'Bid'
        : 'Ask'
      : growlItem.data.nearLeg.currencyIndex === 1
        ? 'Ask'
        : 'Bid';

  const tradeId = growlItem.status === 'confirmed' ? growlItem.tradeIds?.[0] : undefined;

  const blotterEntry = useAppSelector((state) =>
    isDefined(tradeId) ? blotterSlice.selectors.getCashEntryById(state, tradeId) : undefined,
  );

  return (
    <>
      <Toast.Header className={`bg-${color} text-${color} bg-opacity-10`} data-e2e="growl-header">
        <i className="icon icon-sm me-2">{icon}</i>
        <strong className="me-auto">
          <FormattedMessage id={`growl.status.${growlItem.status}`} defaultMessage={growlItem.status || 'Unknown'} />
        </strong>
      </Toast.Header>

      <Toast.Body>
        <div className="text-start" data-e2e="growl-message">
          <FormattedMessage
            id={
              growlItem.data.isNonDeliverable
                ? `growl.FxSwap.NDS.${growlItem.data.way}.${isExternalUser ? 'externalUser' : 'internalUser'}.message`
                : `growl.FxSwap.${growlItem.data.way}.${isExternalUser ? 'externalUser' : 'internalUser'}.message`
            }
            values={{
              currencyPair: `${growlItem.data.currency1}/${growlItem.data.currency2}`,
              rate: growlItem.data.swapPoints,
              isRollover: growlItem.data.type === 'rollover',
              isPredeliver: growlItem.data.type === 'predeliver',
            }}
          />
        </div>

        {growlItem.status === 'not-integrated' && <GrowlIntegrationDelay />}

        {growlItem.status === 'rejected' && (
          <GrowlError
            errorCode={growlItem.errorCode}
            multipassErrorCode={growlItem.multipassErrorCode}
            reason={growlItem.reason}
            sessionId={growlItem.sessionId}
          />
        )}

        <Panel labelId={'growl.Cash.dealDetails'} data-e2e="growl-details-toggle-button">
          <GrowlItem labelId="growl.Cash.swapPoints" data-e2e="growl-swap-points">
            <FormattedNumber
              value={growlItem.data.swapPoints}
              minimumFractionDigits={3}
              maximumFractionDigits={3}
              signDisplay="always"
            />
          </GrowlItem>

          <GrowlItem labelId="growl.Cash.account" data-e2e="growl-swap-account">
            {accountName}
          </GrowlItem>

          <GrowlItem labelId="growl.Cash.tradeDate" data-e2e="growl-swap-trade-date">
            <FormattedFXDate value={growlItem.data.tradeDate} dateStyle="medium" withHours />
          </GrowlItem>

          <GrowlItem labelId="growl.Cash.tradeId" data-e2e="growl-swap-trade-id">
            {growlItem.status === 'confirmed' ? (
              growlItem.tradeIds[0]
            ) : growlItem.status === 'not-integrated' ? (
              <FormattedMessage id="growl.tradeId.notYetIntegrated" />
            ) : (
              <FormattedMessage id="growl.FxSwap.pendingTradeId" />
            )}
          </GrowlItem>

          <GrowlLegs
            nameLabel={'Notional'}
            valueLabel={'Price'}
            dateLabel={'Date'}
            valuePrecision={growlItem.data.currencyPairPrecision + 2}
            legs={[
              {
                name: 'Near leg',
                label: `${effectiveWay === 'Bid' ? 'Buy' : 'Sell'} ${formatNumber(growlItem.data.nearLeg.notional)} ${growlItem.data.nearLeg.currencyIndex === 1 ? growlItem.data.currency1 : growlItem.data.currency2}`,
                contraLabel: isDefined(blotterEntry?.contraAmount)
                  ? `${effectiveWay === 'Bid' ? 'Sell' : 'Buy'} ${formatNumber(blotterEntry?.contraAmount)} ${growlItem.data.nearLeg.currencyIndex === 1 ? growlItem.data.currency2 : growlItem.data.currency1}`
                  : undefined,
                value: blotterEntry?.nearRate ?? growlItem.data.nearLeg.price,
                date: blotterEntry?.nearDate ?? growlItem.data.nearLeg.date,
                'data-e2e': 'grow-swap-near-leg',
              },
              {
                name: 'Far leg',
                label: `${effectiveWay === 'Ask' ? 'Buy' : 'Sell'} ${formatNumber(growlItem.data.farLeg.notional)} ${growlItem.data.farLeg.currencyIndex === 1 ? growlItem.data.currency1 : growlItem.data.currency2}`,
                contraLabel: isDefined(blotterEntry?.farContraAmount)
                  ? `${effectiveWay === 'Ask' ? 'Sell' : 'Buy'} ${formatNumber(blotterEntry?.farContraAmount)} ${growlItem.data.farLeg.currencyIndex === 1 ? growlItem.data.currency2 : growlItem.data.currency1}`
                  : undefined,
                value: blotterEntry?.farRate ?? growlItem.data.farLeg.price,
                date: blotterEntry?.farDate ?? growlItem.data.farLeg.date,
                'data-e2e': 'grow-swap-far-leg',
              },
            ]}
          />
        </Panel>
      </Toast.Body>
    </>
  );
};
